import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { feedbackApi, employeeApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { PageHeader, StatCard, EmptyState, Spinner, Modal, ConfirmModal } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  PlusIcon,
  InboxIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ChatBubbleLeftRightIcon,
  EyeSlashIcon,
  TrashIcon,
  HandThumbUpIcon,
  QuestionMarkCircleIcon,
  AcademicCapIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';

const TYPE_META = {
  praise:      { label: 'Praise',      classes: 'bg-green-100 text-green-700 border-green-200', icon: HandThumbUpIcon },
  constructive:{ label: 'Constructive',classes: 'bg-amber-100 text-amber-700 border-amber-200', icon: AcademicCapIcon },
  requested:   { label: 'Requested',   classes: 'bg-blue-100 text-blue-700 border-blue-200',    icon: QuestionMarkCircleIcon },
  spontaneous: { label: 'Spontaneous', classes: 'bg-gray-100 text-gray-700 border-gray-200',    icon: MegaphoneIcon },
};

const SUGGESTED_TAGS = ['Ownership', 'Collaboration', 'Delivery', 'Growth', 'Communication', 'Impact'];

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || TYPE_META.spontaneous;
  const Icon = meta.icon;
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border', meta.classes)}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

function TagPill({ tag }) {
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-100">{tag}</span>;
}

function relativeTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function FeedbackCard({ fb, perspective, canDelete, onDelete }) {
  // perspective: 'received' => show giver, 'given' => show receiver
  const isAnon = fb.is_anonymous && perspective === 'received';
  const partner = perspective === 'received' ? fb.giver : fb.receiver;
  const partnerName = isAnon ? 'Anonymous' : (partner?.name || 'Unknown');
  const initial = (partnerName[0] || '?').toUpperCase();
  const label = perspective === 'received' ? 'From' : 'To';

  return (
    <div className="card p-5">
      <div className="flex items-start gap-3">
        <div className={clsx(
          'w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold shrink-0',
          isAnon ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-blue-700'
        )}>
          {isAnon ? <EyeSlashIcon className="w-5 h-5" /> : initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">{label}</span>
              <span className="text-sm font-semibold text-gray-900">{partnerName}</span>
              <TypeBadge type={fb.type} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">{relativeTime(fb.created_at)}</span>
              {canDelete && (
                <button onClick={() => onDelete(fb)} className="p-1 hover:bg-red-50 rounded" title="Delete">
                  <TrashIcon className="w-4 h-4 text-red-500" />
                </button>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-800 mt-2 whitespace-pre-wrap leading-relaxed">{fb.message}</p>
          {(fb.value_tags && fb.value_tags.length > 0) && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {fb.value_tags.map(t => <TagPill key={t} tag={t} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RequestCard({ fb, onDelete, canDelete }) {
  const from = fb.from_employee || fb.requested_from || fb.giver;
  const fromName = from?.name || 'Unknown';
  return (
    <div className="card p-5 border-l-4 border-blue-400">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-wide text-blue-600 font-semibold">Feedback request</span>
            <span className="text-xs text-gray-400">{relativeTime(fb.created_at)}</span>
            {fb.status && (
              <span className={clsx('text-[11px] px-2 py-0.5 rounded capitalize',
                fb.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
              )}>{fb.status}</span>
            )}
          </div>
          <p className="text-sm text-gray-700 mt-1">
            Requested feedback from <span className="font-semibold text-gray-900">{fromName}</span>
          </p>
          {fb.message && <p className="text-sm text-gray-600 mt-2 italic">"{fb.message}"</p>}
        </div>
        {canDelete && (
          <button onClick={() => onDelete(fb)} className="p-1 hover:bg-red-50 rounded" title="Delete">
            <TrashIcon className="w-4 h-4 text-red-500" />
          </button>
        )}
      </div>
    </div>
  );
}

function GiveFeedbackForm({ employees = [], selfId, onSubmit, onClose }) {
  const [form, setForm] = useState({
    receiver_id: '',
    type: 'praise',
    message: '',
    value_tags: [],
    is_anonymous: false,
  });
  const msgLen = form.message.trim().length;
  const valid = form.receiver_id && msgLen >= 10;

  const toggleTag = (t) => setForm(f => ({
    ...f,
    value_tags: f.value_tags.includes(t) ? f.value_tags.filter(x => x !== t) : [...f.value_tags, t],
  }));

  return (
    <div className="space-y-4">
      <div>
        <label className="label">To</label>
        <select className="input" value={form.receiver_id} onChange={e => setForm(f => ({ ...f, receiver_id: e.target.value }))}>
          <option value="">Select teammate…</option>
          {employees.filter(e => String(e.id) !== String(selfId)).map(e => (
            <option key={e.id} value={e.id}>{e.name}{e.department?.name ? ` · ${e.department.name}` : ''}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="label">Type</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(TYPE_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const active = form.type === key;
            return (
              <button key={key} type="button" onClick={() => setForm(f => ({ ...f, type: key }))}
                className={clsx('flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs font-medium transition-colors',
                  active ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>
                <Icon className="w-4 h-4" />
                {meta.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-xs text-indigo-800">
        <p className="font-semibold flex items-center gap-1.5 mb-1"><SparklesIcon className="w-3.5 h-3.5" /> SBI framework</p>
        <p><span className="font-semibold">Situation</span> · when and where it happened.</p>
        <p><span className="font-semibold">Behaviour</span> · what the person specifically did.</p>
        <p><span className="font-semibold">Impact</span> · the effect it had on you, the team, or outcomes.</p>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <label className="label">Message</label>
          <span className={clsx('text-xs', msgLen >= 10 ? 'text-green-600' : 'text-gray-400')}>
            {msgLen}/10 min
          </span>
        </div>
        <textarea
          className="input h-32 resize-none"
          value={form.message}
          onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
          placeholder="During last week's launch, you stepped in to coordinate QA which unblocked the release a day early…"
        />
      </div>

      <div>
        <label className="label">Value tags</label>
        <div className="flex flex-wrap gap-2">
          {SUGGESTED_TAGS.map(t => {
            const active = form.value_tags.includes(t);
            return (
              <button key={t} type="button" onClick={() => toggleTag(t)}
                className={clsx('px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                  active ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>
                {t}
              </button>
            );
          })}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
        <input type="checkbox" className="rounded border-gray-300" checked={form.is_anonymous} onChange={e => setForm(f => ({ ...f, is_anonymous: e.target.checked }))} />
        Send anonymously
      </label>

      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button disabled={!valid} onClick={() => onSubmit(form)} className="btn-primary disabled:opacity-40">
          <PaperAirplaneIcon className="w-4 h-4" /> Send feedback
        </button>
      </div>
    </div>
  );
}

function RequestFeedbackForm({ employees = [], selfId, onSubmit, onClose }) {
  const [form, setForm] = useState({ from_employee_id: '', message: '' });
  const valid = form.from_employee_id && form.message.trim().length >= 10;
  return (
    <div className="space-y-4">
      <div>
        <label className="label">Ask feedback from</label>
        <select className="input" value={form.from_employee_id} onChange={e => setForm(f => ({ ...f, from_employee_id: e.target.value }))}>
          <option value="">Select teammate…</option>
          {employees.filter(e => String(e.id) !== String(selfId)).map(e => (
            <option key={e.id} value={e.id}>{e.name}{e.department?.name ? ` · ${e.department.name}` : ''}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="label">What would you like feedback on?</label>
        <textarea
          className="input h-28 resize-none"
          value={form.message}
          onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
          placeholder="I'd love feedback on how I ran the sprint planning last week — clarity, pacing, and decision-making…"
        />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button disabled={!valid} onClick={() => onSubmit(form)} className="btn-primary disabled:opacity-40">
          Send request
        </button>
      </div>
    </div>
  );
}

export default function FeedbackHub() {
  const { user } = useAuth();
  const qc       = useQueryClient();
  const [tab, setTab]                 = useState('received'); // received | given | requests
  const [showGive, setShowGive]       = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [deletingId, setDeletingId]   = useState(null);

  const selfId = user?.employee_id || user?.id;

  const { data: feedback = [], isLoading } = useQuery({
    queryKey: ['feedback-all', selfId],
    queryFn:  () => feedbackApi.list().then(r => r.data?.data || r.data || []),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees-lite'],
    queryFn:  () => employeeApi.list().then(r => r.data?.data || r.data || []),
  });

  const createMut = useMutation({
    mutationFn: (data) => feedbackApi.create(data),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['feedback-all'] }); setShowGive(false); toast.success('Feedback sent'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const requestMut = useMutation({
    mutationFn: (data) => feedbackApi.request(data),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['feedback-all'] }); setShowRequest(false); toast.success('Request sent'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => feedbackApi.delete(id),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['feedback-all'] }); setDeletingId(null); toast.success('Deleted'); },
    onError:    (e) => { toast.error(e.response?.data?.message || 'Failed'); setDeletingId(null); },
  });

  const { received, given, requests, stats } = useMemo(() => {
    const isMine = (v) => String(v) === String(selfId);
    const received = [];
    const given    = [];
    const requests = [];

    for (const fb of feedback) {
      if (fb.type === 'requested' && isMine(fb.requester_id || fb.giver_id)) {
        requests.push(fb);
        continue;
      }
      if (isMine(fb.receiver_id)) received.push(fb);
      if (isMine(fb.giver_id))    given.push(fb);
    }

    const quarterStart = new Date();
    quarterStart.setMonth(Math.floor(quarterStart.getMonth() / 3) * 3, 1);
    quarterStart.setHours(0, 0, 0, 0);

    const stats = {
      totalReceived: received.length,
      givenThisQuarter: given.filter(f => new Date(f.created_at) >= quarterStart).length,
      anonReceived: received.filter(f => f.is_anonymous).length,
      kudos: received.filter(f => f.type === 'praise').length,
    };

    return { received, given, requests, stats };
  }, [feedback, selfId]);

  const tabCounts = { received: received.length, given: given.length, requests: requests.length };

  const renderList = (list, perspective) => {
    if (list.length === 0) {
      return <EmptyState
        title={perspective === 'received' ? 'No feedback yet' : perspective === 'given' ? 'Nothing sent yet' : 'No outstanding requests'}
        description={perspective === 'received'
          ? 'Feedback from teammates and managers will show up here.'
          : perspective === 'given'
            ? 'Recognise good work — your first piece of feedback is one click away.'
            : 'Proactively request feedback to accelerate your growth.'}
        action={
          perspective === 'requests'
            ? <button className="btn-primary" onClick={() => setShowRequest(true)}><ChatBubbleLeftRightIcon className="w-4 h-4" /> Request feedback</button>
            : <button className="btn-primary" onClick={() => setShowGive(true)}><PlusIcon className="w-4 h-4" /> Give feedback</button>
        }
      />;
    }
    const sorted = [...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return (
      <div className="space-y-3">
        {sorted.map(fb =>
          perspective === 'requests'
            ? <RequestCard key={fb.id} fb={fb} canDelete onDelete={(x) => setDeletingId(x.id)} />
            : <FeedbackCard key={fb.id} fb={fb} perspective={perspective}
                canDelete={perspective === 'given'}
                onDelete={(x) => setDeletingId(x.id)} />
        )}
      </div>
    );
  };

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Feedback hub"
        subtitle="Exchange continuous, value-based feedback with your team."
        actions={
          <div className="flex gap-2">
            <button onClick={() => setShowRequest(true)} className="btn-secondary">
              <ChatBubbleLeftRightIcon className="w-4 h-4" /> Request feedback
            </button>
            <button onClick={() => setShowGive(true)} className="btn-primary">
              <PlusIcon className="w-4 h-4" /> Give feedback
            </button>
          </div>
        }
      />

      {/* Top stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Received"          value={stats.totalReceived}    color="blue"   icon={InboxIcon} />
        <StatCard title="Given this quarter" value={stats.givenThisQuarter} color="purple" icon={PaperAirplaneIcon} />
        <StatCard title="Anonymous received" value={stats.anonReceived}     color="amber"  icon={EyeSlashIcon} />
        <StatCard title="Kudos received"     value={stats.kudos}            color="green"  icon={HandThumbUpIcon} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-gray-200">
        {[
          ['received', `Received (${tabCounts.received})`],
          ['given',    `Given (${tabCounts.given})`],
          ['requests', `Requests (${tabCounts.requests})`],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={clsx('px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'received' && renderList(received, 'received')}
      {tab === 'given'    && renderList(given,    'given')}
      {tab === 'requests' && renderList(requests, 'requests')}

      {/* Give modal */}
      <Modal open={showGive} onClose={() => setShowGive(false)} title="Give feedback" size="lg">
        <GiveFeedbackForm
          employees={employees}
          selfId={selfId}
          onSubmit={(data) => createMut.mutate(data)}
          onClose={() => setShowGive(false)}
        />
      </Modal>

      {/* Request modal */}
      <Modal open={showRequest} onClose={() => setShowRequest(false)} title="Request feedback" size="md">
        <RequestFeedbackForm
          employees={employees}
          selfId={selfId}
          onSubmit={(data) => requestMut.mutate(data)}
          onClose={() => setShowRequest(false)}
        />
      </Modal>

      <ConfirmModal
        open={!!deletingId}
        onClose={() => setDeletingId(null)}
        onConfirm={() => deleteMut.mutate(deletingId)}
        title="Delete feedback?"
        message="This will permanently remove the feedback entry. This cannot be undone."
        danger
      />
    </div>
  );
}
